import { useMemo } from "react";
import type { Column } from "@legal-500/legal500-design-system";
import { DataTable } from "../../../../components/DataTable/DataTable.js";
import type { ApplyRowResult, ApplyRowStatus } from "../../../../api/roleProvisioning.service.js";
import styles from "./RoleProvisioning.module.css";

/** The per-row outcome table shown after a Role Provisioning run (§5). */

export const STATUS_LABELS: Record<ApplyRowStatus, string> = {
  inserted: "Inserted",
  created: "Created",
  already_has_roles: "Already had roles",
  not_a_member: "Not a member",
  already_exists: "Already exists",
  error: "Error",
};

const PILL_CLASSES: Record<ApplyRowStatus, string> = {
  inserted: styles.pillOk as string,
  created: styles.pillOk as string,
  // Neutral: the endpoint found nothing to do, nothing went wrong.
  already_has_roles: styles.pillNeutral as string,
  // Warnings: the world moved between scan and apply — re-scan picks it up.
  not_a_member: styles.pillWarning as string,
  already_exists: styles.pillWarning as string,
  error: styles.pillError as string,
};

/** Tallies results by status, for the summary line. */
export function countByStatus(results: ApplyRowResult[]): (status: ApplyRowStatus) => number {
  const tally = new Map<ApplyRowStatus, number>();
  for (const result of results) {
    tally.set(result.status, (tally.get(result.status) ?? 0) + 1);
  }
  return status => tally.get(status) ?? 0;
}

type ResultsTableProps = {
  results: ApplyRowResult[];
  /** The scanned email behind a line number, so the table reads as people, not ids. */
  emailFor: (lineNumber: number) => string | null;
};

type TableRow = {
  id: string;
  lineNumber: number;
  email: string | null;
  userId: string | null;
  organisationId: string | null;
  status: ApplyRowStatus;
  rolesInserted: string[];
};

function shortId(id: string | null): string {
  // The first eight characters are enough to tell rows apart by eye; the
  // full id stays a hover away rather than widening every row.
  return id === null ? "—" : id.slice(0, 8);
}

export function ResultsTable({ results, emailFor }: ResultsTableProps) {
  const rows = useMemo<TableRow[]>(
    () =>
      results.map((result, index) => ({
        // Index-prefixed: a malformed response could repeat a line number.
        id: `${index}-${result.lineNumber}`,
        lineNumber: result.lineNumber,
        email: emailFor(result.lineNumber),
        userId: result.userId,
        organisationId: result.organisationId,
        status: result.status,
        rolesInserted: result.rolesInserted ?? [],
      })),
    [results, emailFor],
  );

  const columns = useMemo<Column<TableRow>[]>(
    () => [
      {
        header: "Line",
        accessor: "lineNumber",
        render: (_value, row) => <span className={styles.mono}>{row.lineNumber}</span>,
      },
      {
        header: "Email",
        accessor: "email",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.email ?? "—"}</span>,
      },
      {
        header: "User id",
        accessor: "userId",
        render: (_value, row) => (
          <span className={styles.mono} title={row.userId ?? undefined}>
            {shortId(row.userId)}
          </span>
        ),
      },
      {
        header: "Organisation id",
        accessor: "organisationId",
        render: (_value, row) => (
          <span className={styles.mono} title={row.organisationId ?? undefined}>
            {shortId(row.organisationId)}
          </span>
        ),
      },
      {
        header: "Status",
        accessor: "status",
        render: (_value, row) => (
          <span className={`${styles.pill} ${PILL_CLASSES[row.status]}`}>
            {STATUS_LABELS[row.status]}
          </span>
        ),
      },
      {
        header: "Roles inserted",
        accessor: "rolesInserted",
        wrap: true,
        render: (_value, row) => (
          <span className={styles.mono}>
            {row.rolesInserted.length === 0 ? "—" : row.rolesInserted.join(", ")}
          </span>
        ),
      },
    ],
    [],
  );

  return <DataTable data={rows} columns={columns} emptyMessage="No results." />;
}
